/**
 * useInProgressLockWarning Hook
 * 
 * Detects when a condition is added to a task that:
 * 1. Was previously unlocked (no conditions or all conditions met)
 * 2. Has an "in progress" status
 * 3. Will become LOCKED due to the new condition
 * 
 * When this edge case is detected, it shows a warning modal asking
 * the user if they want to switch the task back to a "not started" status.
 */
import { useState, useCallback, useRef } from 'react';
import { getStatusCategory } from '../FlowchartView/utils/statusConfig';
import { calculateLockStates } from '../FlowchartView/utils/dependencyUtils';
import { getAllConditionTaskIds } from '../shared/ConditionsDropdown/utils/conditionHelpers';
import { findPreferredStatus } from '../FlowchartView/utils/statusManagement';

/**
 * Check if a task was previously unlocked
 * A task is unlocked if it has no conditions, or all its conditions are satisfied
 */
const wasTaskUnlocked = (task, allTasks, nestedSprints, statuses) => {
  // Calculate current lock state
  const lockStates = calculateLockStates(allTasks, nestedSprints, statuses);
  const lockState = lockStates[task.id];
  
  // Task is unlocked if not locked
  return !lockState?.isLocked;
};

/**
 * Check if adding new conditions would lock the task
 * Returns both whether it would be locked and the full blocking task objects
 */
const wouldConditionsLockTask = (task, newConditions, allTasks, nestedSprints, statuses) => {
  // Create a temporary task with the new conditions
  const tempTask = { ...task, unlockConditions: newConditions, dependsOn: [] };
  
  // Create a temporary tasks array with the updated task
  const tempTasks = allTasks.map(t => t.id === task.id ? tempTask : t);
  
  // Calculate lock state with new conditions
  const lockStates = calculateLockStates(tempTasks, nestedSprints, statuses);
  const lockState = lockStates[task.id];
  
  // Get full task objects for blocking tasks (lockState.blockingTasks contains partial info)
  const blockingTaskIds = (lockState?.blockingTasks || []).map(bt => bt.id);
  const fullBlockingTasks = blockingTaskIds
    .map(id => allTasks.find(t => t.id === id))
    .filter(Boolean);
  
  return {
    wouldBeLocked: lockState?.isLocked || false,
    blockingTasks: fullBlockingTasks,
  };
};

export const useInProgressLockWarning = ({
  tasks,
  statuses,
  nestedSprints = [],
  allTags = [],
  onStatusChange,
  flowchartRef, // Optional - for triggering auto-layout after modal closes
}) => {
  // Modal state - now stores full task objects
  const [modalState, setModalState] = useState({
    isOpen: false,
    affectedTask: null,      // Full task object
    blockingTasks: [],       // Array of full task objects
    pendingConditions: null,
  });
  
  // Ref to store the original onConditionsChange callback
  const pendingConditionsChangeRef = useRef(null);
  // Ref to store the source and target task IDs for auto-layout
  const pendingConnectionRef = useRef(null);
  
  /**
   * Wraps the conditions change to check for the edge case
   * Returns a function that should be called instead of the original onConditionsChange
   */
  const checkAndHandleConditionsChange = useCallback((taskId, newConditions, originalOnConditionsChange, options) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      // Task not found, just proceed with the change
      return originalOnConditionsChange?.(taskId, newConditions, options);
    }
    
    // Check if task has an "in progress" status
    const statusCategory = getStatusCategory(task.status, statuses);
    if (statusCategory !== 'in_progress') {
      // Not in progress, no need to show warning
      return originalOnConditionsChange?.(taskId, newConditions, options);
    }
    
    // Check if task was previously unlocked
    if (!wasTaskUnlocked(task, tasks, nestedSprints, statuses)) {
      // Task was already locked, no need to show warning
      return originalOnConditionsChange?.(taskId, newConditions, options);
    }
    
    // Check if the new conditions would lock the task
    const { wouldBeLocked, blockingTasks } = wouldConditionsLockTask(
      task, newConditions, tasks, nestedSprints, statuses
    );
    
    if (!wouldBeLocked) {
      // New conditions don't lock the task, proceed normally
      return originalOnConditionsChange?.(taskId, newConditions, options);
    }
    
    // Edge case detected! Show the warning modal
    // Store the pending change
    pendingConditionsChangeRef.current = {
      taskId,
      newConditions,
      originalOnConditionsChange,
      options,
    };
    
    // Store connection info for auto-layout (the new condition's source task)
    // The new conditions array should have the newly added condition
    const existingConditionIds = new Set(getAllConditionTaskIds(task.unlockConditions));
    const newConditionIds = getAllConditionTaskIds(newConditions);
    const newConditionTaskId = newConditionIds.find(id => !existingConditionIds.has(id));
    const newCondition = newConditionTaskId ? { taskId: newConditionTaskId } : null;
    if (newCondition) {
      pendingConnectionRef.current = {
        sourceId: newCondition.taskId,
        targetId: taskId,
      };
    }
    
    setModalState({
      isOpen: true,
      affectedTask: task,
      blockingTasks,
      pendingConditions: newConditions,
    });
  }, [tasks, statuses, nestedSprints]);
  
  /**
   * Trigger auto-layout after conditions are applied
   */
  const triggerAutoLayout = useCallback(() => {
    const connection = pendingConnectionRef.current;
    if (connection && flowchartRef?.current?.autoLayout) {
      // Small delay to let the conditions change propagate
      setTimeout(() => {
        // Get connected nodes for both source and target
        const nodeIds = new Set([connection.sourceId, connection.targetId]);
        flowchartRef.current.autoLayout(nodeIds);
      }, 50);
    }
    pendingConnectionRef.current = null;
  }, [flowchartRef]);
  
  /**
   * Handle user confirming they want to switch to "To Do"
   */
  const handleConfirmSwitchToToDo = useCallback(() => {
    const pending = pendingConditionsChangeRef.current;
    if (!pending) {
      setModalState(prev => ({ ...prev, isOpen: false }));
      return;
    }
    
    const { taskId, newConditions, originalOnConditionsChange, options } = pending;
    
    // Apply the conditions change
    originalOnConditionsChange?.(taskId, newConditions, options);
    
    // Find the preferred "not started" status
    const toDoStatus = findPreferredStatus(statuses, 'not_started');
    if (toDoStatus && onStatusChange) {
      // Switch the task to "To Do" status
      onStatusChange(taskId, toDoStatus.id);
    }
    
    // Trigger auto-layout
    triggerAutoLayout();
    
    // Clear state
    pendingConditionsChangeRef.current = null;
    setModalState({
      isOpen: false,
      affectedTask: null,
      blockingTasks: [],
      pendingConditions: null,
    });
  }, [statuses, onStatusChange, triggerAutoLayout]);
  
  /**
   * Handle user choosing to keep the current status
   */
  const handleKeepCurrentStatus = useCallback(() => {
    const pending = pendingConditionsChangeRef.current;
    if (!pending) {
      setModalState(prev => ({ ...prev, isOpen: false }));
      return;
    }
    
    const { taskId, newConditions, originalOnConditionsChange, options } = pending;
    
    // Apply the conditions change without changing status
    originalOnConditionsChange?.(taskId, newConditions, options);
    
    // Trigger auto-layout
    triggerAutoLayout();
    
    // Clear state
    pendingConditionsChangeRef.current = null;
    setModalState({
      isOpen: false,
      affectedTask: null,
      blockingTasks: [],
      pendingConditions: null,
    });
  }, [triggerAutoLayout]);
  
  /**
   * Creates a wrapped version of onConditionsChange that checks for the edge case
   */
  const createWrappedConditionsChange = useCallback((originalOnConditionsChange) => {
    return (taskId, newConditions, options) =>
      checkAndHandleConditionsChange(taskId, newConditions, originalOnConditionsChange, options);
  }, [checkAndHandleConditionsChange]);
  
  return {
    // Modal state - now includes full task objects, statuses, and allTags
    inProgressLockWarningModal: {
      isOpen: modalState.isOpen,
      affectedTask: modalState.affectedTask,
      blockingTasks: modalState.blockingTasks,
      statuses,
      allTags,
      onConfirm: handleConfirmSwitchToToDo,
      onCancel: handleKeepCurrentStatus,
    },
    
    // Utility to wrap onConditionsChange
    createWrappedConditionsChange,
  };
};

export default useInProgressLockWarning;
